/**
 * Date formatting utilities for handling different date format configurations
 */

export type DateFormatType =
  | "month_day_year"
  | "day_month_year"
  | "year_month_day"

interface DateComponents {
  year: number
  month: number
  day: number
}

/**
 * Parse a date string based on the specified format
 * @param dateString - The date string to parse (e.g., "12/31/2024", "31/12/2024", "2024/12/31")
 * @param format - The format configuration
 * @returns DateComponents with year, month, day or null if invalid
 */
export const parseDateString = (
  dateString: string,
  format: DateFormatType = "month_day_year"
): DateComponents | null => {
  if (!dateString || !dateString.includes("/")) {
    return null
  }

  const parts = dateString.split("/").map(Number)
  if (parts.length !== 3 || parts.some(isNaN)) {
    return null
  }

  let year: number, month: number, day: number

  switch (format) {
    case "day_month_year":
      ;[day, month, year] = parts
      break
    case "year_month_day":
      ;[year, month, day] = parts
      break
    case "month_day_year":
    default:
      ;[month, day, year] = parts
      break
  }

  // Validate the components
  if (!year || !month || !day) {
    return null
  }

  // Basic validation for reasonable date ranges
  if (month < 1 || month > 12) {
    return null
  }

  if (day < 1 || day > 31) {
    return null
  }

  // Year validation (reasonable range)
  if (year < 1900 || year > 2200) {
    return null
  }

  return { year, month, day }
}

/**
 * Convert DateComponents to a JavaScript Date object
 * @param components - The date components
 * @returns Date object or null if invalid
 */
export const componentsToDate = (
  components: DateComponents | null
): Date | null => {
  if (!components) {
    return null
  }

  const { year, month, day } = components
  const date = new Date(year, month - 1, day) // month is 0-indexed in Date constructor

  // Validate the date is valid (handles things like Feb 31)
  if (isNaN(date.getTime())) {
    return null
  }

  // Check if the date components match what was created
  // (this catches invalid dates like Feb 31 that get rolled over)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }

  return date
}

/**
 * Parse a date string and return a Date object
 * @param dateString - The date string to parse
 * @param format - The format configuration
 * @returns Date object or null if invalid
 */
export const parseFormattedDate = (
  dateString: string,
  format: DateFormatType = "month_day_year"
): Date | null => {
  const components = parseDateString(dateString, format)
  return componentsToDate(components)
}

/**
 * Format a Date object as a string for payload submission
 * @param date - The Date object to format
 * @param format - The format configuration
 * @returns Formatted date string with zero-padding
 */
export const formatDateForPayload = (
  date: Date,
  format: DateFormatType = "month_day_year"
): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0") // getMonth() returns 0-11
  const day = String(date.getDate()).padStart(2, "0")

  switch (format) {
    case "day_month_year":
      return `${day}/${month}/${year}`
    case "year_month_day":
      return `${year}/${month}/${day}`
    case "month_day_year":
    default:
      return `${month}/${day}/${year}`
  }
}

/**
 * Format a Date object for display to the user
 * @param date - The Date object to format
 * @param format - The format configuration
 * @param separator - The separator character (default: "/")
 * @returns Formatted date string for display
 */
export const formatDateForDisplay = (
  date: Date,
  format: DateFormatType = "month_day_year",
  separator: string = "/"
): string => {
  const year = date.getFullYear()
  const month = date.getMonth() + 1 // getMonth() returns 0-11
  const day = date.getDate()

  switch (format) {
    case "day_month_year":
      return `${day}${separator}${month}${separator}${year}`
    case "year_month_day":
      return `${year}${separator}${month}${separator}${day}`
    case "month_day_year":
    default:
      return `${month}${separator}${day}${separator}${year}`
  }
}

/**
 * Validate a date string based on the specified format
 * @param dateString - The date string to validate
 * @param format - The format configuration
 * @returns true if valid, false otherwise
 */
export const isValidDateString = (
  dateString: string,
  format: DateFormatType = "month_day_year"
): boolean => {
  return parseFormattedDate(dateString, format) !== null
}
